import { useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Skeleton } from '../../components/ui/Skeleton';
import { useProjects } from '../../hooks/useProjects';
import { useGenerateReportePlanillas } from '../../hooks/useReportes';
import { toast } from '../../components/ui/Toast';
import { FileText, FileSpreadsheet, Search, X } from 'lucide-react';

export function ReportesPage() {
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [loadingFormat, setLoadingFormat] = useState<'pdf' | 'xlsx' | null>(null);
  const [desglosar, setDesglosar] = useState(false);

  const { data: projectsData, isLoading: loadingProjects } = useProjects({ page: 1, limit: 500 });
  const generateReport = useGenerateReportePlanillas(selectedProjectId);

  const projects = projectsData?.data ?? [];
  const suggestions = projects.filter((p) => {
    if (!projectSearch) return true;
    const q = projectSearch.toLowerCase();
    return p.nombre.toLowerCase().includes(q) || p.codigo.toLowerCase().includes(q);
  }).slice(0, 8);

  const handleSelectProject = (p: typeof projects[0]) => {
    setSelectedProjectId(p.id);
    setProjectSearch(`${p.codigo} — ${p.nombre}`);
    setShowSuggestions(false);
  };

  const handleClearProject = () => {
    setSelectedProjectId('');
    setProjectSearch('');
    setShowSuggestions(false);
  };

  const canGenerate = selectedProjectId && desde && hasta;

  const handleExportPdf = async () => {
    if (!canGenerate) return;
    setLoadingFormat('pdf');
    try {
      await generateReport.mutateAsync({ desde, hasta, format: 'pdf', desglosar });
      toast('success', 'PDF generado');
    } catch {
      toast('error', 'Error al generar PDF');
    } finally {
      setLoadingFormat(null);
    }
  };

  const handleExportXlsx = async () => {
    if (!canGenerate) return;
    setLoadingFormat('xlsx');
    try {
      await generateReport.mutateAsync({ desde, hasta, format: 'xlsx', desglosar });
      toast('success', 'Excel descargado');
    } catch {
      toast('error', 'Error al generar Excel');
    } finally {
      setLoadingFormat(null);
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <h1 className="text-2xl font-bold text-brand-dark">Reportes</h1>

      {/* Filters — relative z-10 ensures the combobox dropdown (z-30) stacks above
          the empty-state card below. backdrop-filter on glass-card creates a new
          stacking context, so the z-index must be lifted at this wrapper level. */}
      <div className="relative z-10">
      <Card>
        <h2 className="mb-4 text-lg font-semibold text-brand-dark">
          Reporte de Planillas
        </h2>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Proyecto</label>
            {loadingProjects ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-200 bg-white/80 py-2 pl-9 pr-9 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                  placeholder="Buscar proyecto por nombre o código..."
                  value={projectSearch}
                  onChange={(e) => {
                    setProjectSearch(e.target.value);
                    setSelectedProjectId('');
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                />
                {selectedProjectId && (
                  <button
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onMouseDown={(e) => { e.preventDefault(); handleClearProject(); }}
                  >
                    <X size={14} />
                  </button>
                )}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-30 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-60 overflow-y-auto">
                    {suggestions.map((p) => (
                      <button
                        key={p.id}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex flex-col border-b border-gray-50 last:border-0"
                        onMouseDown={(e) => { e.preventDefault(); handleSelectProject(p); }}
                      >
                        <span className="text-sm font-medium text-gray-800">{p.nombre}</span>
                        <span className="text-xs text-gray-400">{p.codigo}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <Input
            label="Desde"
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
          <Input
            label="Hasta"
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
        </div>

        <div className="mt-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={desglosar}
              onChange={(e) => setDesglosar(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue/30"
            />
            Desglosar por planilla individual
          </label>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <Button
            variant="primary"
            onClick={handleExportPdf}
            isLoading={loadingFormat === 'pdf'}
            disabled={!canGenerate || loadingFormat !== null}
          >
            <FileText size={16} className="mr-2" />
            Exportar PDF
          </Button>
          <Button
            variant="secondary"
            onClick={handleExportXlsx}
            isLoading={loadingFormat === 'xlsx'}
            disabled={!canGenerate || loadingFormat !== null}
          >
            <FileSpreadsheet size={16} className="mr-2" />
            Exportar Excel
          </Button>
        </div>
      </Card>
      </div>

      {/* Empty state */}
      {!canGenerate && (
        <Card>
          <p className="py-8 text-center text-sm text-gray-400">
            Selecciona un proyecto y rango de fechas para generar el reporte
          </p>
        </Card>
      )}

    </div>
  );
}
